import Modal from "@/ui/Modal/Modal";
import React, { useEffect, useMemo, useRef, useState } from "react";
import { PaymentModalContent } from "@/features/PaymentsModal/PaymentStates";
import { useSubscriptionStore } from "@/store/useSubscriptionStore";
import { usePaymentStatusStore } from "@/store/paymentStatus";
import { SettingsIcon } from "@/assets/icons";
import { AnimatePresence } from "framer-motion";
import { useSearchParams } from "react-router-dom";
import { useConfettiShow } from "@/utils/hooks/useConfettiShow";
import { useRampHaptic } from "@/utils/hooks/useRampHaptic";
import Confetti, { ConfettiHandle } from "@/components/Confetti/Confetti";
import { usePaymentOptionStore } from "@/store/paymentOption";
import { showNotification } from "@/utils/network";
import { useTranslation } from "react-i18next";
import cls from "./styles.module.scss";

export default function PaymentsModal() {
  const { t } = useTranslation();
  const confettiRef = useRef<ConfettiHandle>(null);
  const [searchParams, setSearchParams] = useSearchParams();
  const isPaymentConfirmed = useMemo(() => {
    return searchParams.get("tgWebAppStartParam") === "payment_confirmed_true";
  }, [searchParams]);

  const isNewCardAdded = useMemo(() => {
    return searchParams.get("tgWebAppStartParam") === "card_changed_true";
  }, [searchParams]);

  const { isSubscriptionActive } = useSubscriptionStore();

  const { goToStep } = usePaymentOptionStore();

  const { setShowedResult } = usePaymentStatusStore();
  const [isOpen, setIsOpen] = useState(false);

  const { fireworkParty, fireFromCorners } = useConfettiShow(confettiRef);
  const { ramp } = useRampHaptic();

  const onClose = () => {
    if (!isSubscriptionActive) return;

    setShowedResult(true);

    if (isPaymentConfirmed || isNewCardAdded) {
      setSearchParams(
        (p) => {
          p.delete("tgWebAppStartParam");
          return p;
        },
        { replace: true }
      );
    }
    setIsOpen(false);
  };

  const onOpen = () => {
    goToStep("firstPage");
    setIsOpen(true);
  };

  const onConfetti = () => {
    setIsOpen(true);
    setTimeout(() => {
      fireFromCorners();
      ramp(25, 15);
    }, 750);
    setTimeout(() => {
      fireworkParty(8, 250);
    }, 1350);
  };

  const addNewCard = () => {
    goToStep("secondPage");
    setIsOpen(true);
    showNotification({
      type: "success",
      text: t("paymentOptions.addNewPaymentOption"),
    });
  };

  useEffect(() => {
    if (isNewCardAdded && isSubscriptionActive) addNewCard();
    if (isPaymentConfirmed && isSubscriptionActive) onConfetti();
  }, []);

  return (
    <>
      <button type="button" onClick={onOpen} className={cls.openBtn}>
        <SettingsIcon />
      </button>
      <Modal
        hideCloseBtn={!isSubscriptionActive}
        isOpen={isOpen}
        onClose={onClose}
        closeOnBackdropClick={isSubscriptionActive}
      >
        <Confetti ref={confettiRef} />
        <div className={cls.wrapper}>
          <AnimatePresence>
            <PaymentModalContent />
          </AnimatePresence>
        </div>
      </Modal>
    </>
  );
}
